/** Content contract shared by the in-app checklist and lifecycle email. */
export interface CvCompletenessProfile {
  name: string;
  headline: string;
  contactEmail: string;
  phone: string;
  location: string;
  website: string;
  linkedin: string;
  summary: string;
  experience: Array<{ role: string; company: string }>;
  education: Array<{ degree: string; school: string }>;
  skillGroups: Array<{ items: string[] }>;
  highlights: Array<{ title: string; desc: string }>;
  certifications: Array<{ name: string }>;
  languages: string[];
  jobPreferences: {
    openToWork: boolean;
    jobTitles: string[];
    locationTypes: string[];
    preferredLocations: string[];
    employmentTypes: string[];
    searchAreas?: unknown[];
    relocation?: string;
    relocationAreas?: string[];
    noticePeriod?: string;
  };
}

export type CvCompletenessChecklistId =
  | 'header'
  | 'summary'
  | 'experience'
  | 'education'
  | 'skills'
  | 'highlights'
  | 'certifications'
  | 'languages'
  | 'jobPreferences';

export interface CvCompletenessChecklistItem {
  id: CvCompletenessChecklistId;
  completed: boolean;
  countable: boolean;
}

export interface CvCompletenessModel {
  checklistItems: CvCompletenessChecklistItem[];
  checklistDone: number;
  checklistTotal: number;
  completionPercent: number;
}

const CV_COMPLETENESS_ORDER: CvCompletenessChecklistId[] = [
  'header',
  'summary',
  'experience',
  'education',
  'skills',
  'languages',
  'jobPreferences',
  'highlights',
  'certifications',
];

const OPTIONAL_CV_COMPLETENESS_IDS = new Set<CvCompletenessChecklistId>([
  'highlights',
  'certifications',
]);

function hasText(value?: string | null) {
  return typeof value === 'string' && value.trim().length > 0;
}

export function computeCvCompleteness(
  profile: CvCompletenessProfile,
  options: { includeJobPreferences?: boolean } = {},
): CvCompletenessModel {
  const includeJobPreferences = options.includeJobPreferences ?? true;
  // Header counts as filled when the identity essentials are present: a name,
  // a headline/title, and at least one way to reach the person.
  const hasHeader =
    hasText(profile.name) &&
    hasText(profile.headline) &&
    (hasText(profile.contactEmail) ||
      hasText(profile.phone) ||
      hasText(profile.location) ||
      hasText(profile.website) ||
      hasText(profile.linkedin));

  const hasSummary = hasText(profile.summary);

  const hasExperience = profile.experience.some(
    (item) => hasText(item.role) || hasText(item.company),
  );

  const hasEducation = profile.education.some(
    (item) => hasText(item.degree) || hasText(item.school),
  );

  const hasSkills = profile.skillGroups.some((group) =>
    group.items.some((item) => hasText(item)),
  );

  const hasHighlights = profile.highlights.some(
    (item) => hasText(item.title) || hasText(item.desc),
  );

  const hasCertifications = profile.certifications.some((item) =>
    hasText(item.name),
  );

  const hasLanguages = profile.languages.some((item) => hasText(item));

  const hasJobPreferences =
    profile.jobPreferences.openToWork ||
    profile.jobPreferences.jobTitles.some((item) => hasText(item)) ||
    profile.jobPreferences.locationTypes.some((item) => hasText(item)) ||
    profile.jobPreferences.preferredLocations.some((item) => hasText(item)) ||
    profile.jobPreferences.employmentTypes.some((item) => hasText(item)) ||
    (profile.jobPreferences.searchAreas?.length ?? 0) > 0 ||
    hasText(profile.jobPreferences.relocation) ||
    (profile.jobPreferences.relocationAreas ?? []).some((item) =>
      hasText(item),
    ) ||
    hasText(profile.jobPreferences.noticePeriod);

  const map: Record<CvCompletenessChecklistId, boolean> = {
    header: hasHeader,
    summary: hasSummary,
    experience: hasExperience,
    education: hasEducation,
    skills: hasSkills,
    highlights: hasHighlights,
    certifications: hasCertifications,
    languages: hasLanguages,
    jobPreferences: hasJobPreferences,
  };

  const checklistItems = CV_COMPLETENESS_ORDER.filter(
    (id) => includeJobPreferences || id !== 'jobPreferences',
  ).map((id) => ({
    id,
    completed: map[id],
    countable: !OPTIONAL_CV_COMPLETENESS_IDS.has(id),
  }));

  const countedItems = checklistItems.filter((item) => item.countable);
  const checklistDone = countedItems.filter((item) => item.completed).length;
  const checklistTotal = countedItems.length;
  const completionPercent = Math.round(
    (checklistDone / Math.max(checklistTotal, 1)) * 100,
  );

  return { checklistItems, checklistDone, checklistTotal, completionPercent };
}
